/**
 * OpenAI API Service
 * All OpenAI calls go through the Lambda proxy - frontend never calls OpenAI directly.
 * Requires REACT_APP_API_URL to point to your API (Lambda or backend).
 */

interface Message {
  role: 'system' | 'user' | 'assistant';
  content: string | Array<{ type: 'text'; text: string } | { type: 'image_url'; image_url: { url: string } }>;
}

interface ChatCompletionResponse {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
  }>;
}

function getProxyUrl(): string {
  const raw = process.env.REACT_APP_API_URL;
  if (typeof raw !== 'string' || !raw.trim().startsWith('http')) {
    throw new Error(
      'REACT_APP_API_URL is required. Set it to your Lambda/API base URL (e.g. https://xxx.execute-api.us-east-1.amazonaws.com/prod).'
    );
  }
  return raw.trim().replace(/\/+$/, '');
}

export const callOpenAI = async (
  systemPrompt: string,
  userMessage: string,
  conversationHistory: Array<{ text: string; isUser: boolean }> = [],
  imageDataUrl?: string | null
): Promise<string> => {
  const backendUrl = getProxyUrl();

  // Build messages array
  const messages: Message[] = [
    { role: 'system', content: systemPrompt },
  ];

  // Add conversation history (last 10 messages for context)
  conversationHistory.slice(-10).forEach(msg => {
    messages.push({
      role: msg.isUser ? 'user' : 'assistant',
      content: msg.text,
    });
  });

  // Add current user message (with optional image for vision)
  const userContent: Message['content'] = imageDataUrl
    ? [
        { type: 'text', text: userMessage },
        { type: 'image_url', image_url: { url: imageDataUrl } },
      ]
    : userMessage;
  messages.push({ role: 'user', content: userContent });

  try {
    const response = await fetch(`${backendUrl}/api/openai-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages,
        temperature: 0.7,
        max_tokens: 1800,
      }),
    });

    if (!response.ok) {
      let errorMessage = `OpenAI API error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error('OpenAI API error details:', errorData);
        
        // Handle both direct API errors and backend proxy errors
        const actualError = errorData.error || errorData;
        if (typeof actualError === 'string') {
          errorMessage = actualError;
        } else if (actualError.message) {
          errorMessage = actualError.message;
        } else if (actualError.error) {
          errorMessage = actualError.error;
        }
        
        // User-friendly messages (Lambda returns these for 401/429/500)
        if (response.status === 401 || (response.status === 500 && typeof errorMessage === 'string' && /authentication|invalid.*key/i.test(errorMessage))) {
          errorMessage = 'API configuration error. Please contact support.';
        } else if (response.status === 429) {
          errorMessage = 'API rate limit exceeded. Please try again in a moment.';
        } else if (response.status === 500 || response.status === 503) {
          if (errorMessage.includes('Network error') || errorMessage.includes('connect')) {
            errorMessage = 'Network error. Please check your internet connection and try again.';
          } else {
            errorMessage = 'OpenAI service is temporarily unavailable. Please try again in a moment.';
          }
        }
      } catch (parseError) {
        console.error('Failed to parse error response:', parseError);
        const textResponse = await response.text().catch(() => 'Unknown error');
        console.error('Raw error response:', textResponse);
        
        if (textResponse.includes('CORS') || response.status === 0) {
          errorMessage = 'Connection error. Ensure REACT_APP_API_URL points to your deployed Lambda/API.';
        }
      }
      
      throw new Error(errorMessage);
    }

    const data: ChatCompletionResponse = await response.json();
    
    if (!data.choices || data.choices.length === 0) {
      throw new Error('No response from OpenAI API');
    }

    const content = data.choices[0].message.content.trim();
    console.log('OpenAI API success, response length:', content.length);
    return content;
  } catch (error: any) {
    console.error('OpenAI API error:', error);
    
    // Re-throw with original error message
    if (error instanceof Error) {
      throw error;
    }
    
    // Handle network errors or other unexpected errors
    if (error?.message?.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection and try again.');
    }
    
    throw new Error(error?.message || 'An unexpected error occurred while connecting to the AI service.');
  }
};

